/**
 * PlansPage - "My Work" hub
 *
 * Execution-first view: lists steps assigned to the current user across
 * all plans where they are a participant. Primary CTA is "Start from a guide".
 */

import React, {useState, useEffect, useMemo} from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import EditNoteIcon from '@mui/icons-material/EditNote';
import ReplyIcon from '@mui/icons-material/Reply';
import RateReviewIcon from '@mui/icons-material/RateReview';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ExploreIcon from '@mui/icons-material/Explore';
import {useNavigate} from 'react-router-dom';
import {useQuery} from '@apollo/client';
import {GET_MY_ACCESSIBLE_PLANS, GET_PARTICIPANT_WORKSPACE, GET_FEATURED_GUIDES, ORG_JOBS} from '../graphql/queries';
import {useAuth} from '../contexts/AuthContext';
import {useOrg} from '../contexts/OrgContext';
import {PlanStatus} from '../types/generated/graphql';
import {useDocumentTitle} from '../hooks/useDocumentTitle';
import {colors} from '../theme/colors';
import {buildGuideUrl} from '../utils/urls';
import {PlanListRow, PlanListItemData} from '../components/PlanListRow';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AssignedStep {
  stepId: string;
  title: string;
  description: string;
  status: string;
  planId: string;
  planTitle: string;
  updatedAt: string;
  intent?: string | null;
  hasIntake: boolean;
  hasRequestThreads: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function deriveActionLabel(step: AssignedStep): {label: string; icon: React.ReactNode} {
  if (step.hasIntake) return {label: 'Fill out', icon: <EditNoteIcon sx={{fontSize: 16}} />};
  if (step.hasRequestThreads) return {label: 'Reply', icon: <ReplyIcon sx={{fontSize: 16}} />};

  switch (step.intent) {
    case 'COLLECT_DOCUMENT':
      return {label: 'Upload', icon: <UploadFileIcon sx={{fontSize: 16}} />};
    case 'REVIEW_APPROVE':
      return {label: 'Review', icon: <RateReviewIcon sx={{fontSize: 16}} />};
    case 'DATA_ENTRY':
    case 'GATHER_INFO':
      return {label: 'Fill out', icon: <EditNoteIcon sx={{fontSize: 16}} />};
    default:
      return {label: 'Continue', icon: <PlayArrowIcon sx={{fontSize: 16}} />};
  }
}

function statusChipProps(status: string): {label: string; color: 'default' | 'primary' | 'warning'} {
  switch (status) {
    case 'IN_PROGRESS':
      return {label: 'In progress', color: 'primary'};
    case 'BLOCKED':
      return {label: 'Waiting', color: 'warning'};
    case 'NOT_STARTED':
    default:
      return {label: 'Not started', color: 'default'};
  }
}

function formatRelativeDate(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString('en-US', {month: 'short', day: 'numeric'});
}

// ---------------------------------------------------------------------------
// Sub-component: step rows aggregated from participant workspaces
// ---------------------------------------------------------------------------

function useAssignedSteps(plans: any[], user: any) {
  // For each non-owner plan, fetch participant workspace
  const participantPlanIds = useMemo(
    () => (plans || []).filter((p: any) => !p.isOwner && p.status === PlanStatus.Active).map((p: any) => p.id),
    [plans]
  );

  // We'll run individual queries for each plan. For v1 with < ~20 plans this is fine.
  // Each hook must be called unconditionally, so we use a single query per plan via a
  // wrapper component pattern. Instead, we'll aggregate in a child component.
  // But hooks can't be called in a loop. So we use a different strategy:
  // fetch all workspaces via a single useEffect with the Apollo client.
  const [steps, setSteps] = useState<AssignedStep[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Access the Apollo client
  const {client} = useQuery(GET_MY_ACCESSIBLE_PLANS, {skip: true});

  useEffect(() => {
    if (!user || participantPlanIds.length === 0) {
      setSteps([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    async function fetchAll() {
      try {
        const results = await Promise.allSettled(
          participantPlanIds.map((planId: string) =>
            client.query({
              query: GET_PARTICIPANT_WORKSPACE,
              variables: {planId},
              fetchPolicy: 'network-only',
            })
          )
        );

        if (cancelled) return;

        const allSteps: AssignedStep[] = [];
        for (const result of results) {
          if (result.status === 'fulfilled' && result.value.data?.participantWorkspace) {
            const ws = result.value.data.participantWorkspace;
            for (const step of ws.mySteps) {
              if (step.status === 'COMPLETED' || step.status === 'SKIPPED') continue;
              allSteps.push({
                stepId: step.stepId,
                title: step.title,
                description: step.description || '',
                status: step.status,
                planId: ws.planId,
                planTitle: ws.planTitle,
                updatedAt: step.updatedAt || ws.updatedAt,
                intent: null, // participant workspace doesn't expose intent
                hasIntake: false, // not exposed in workspace query
                hasRequestThreads: false,
              });
            }
          }
          // Silently skip failed workspace fetches (e.g. permissions)
        }

        // Sort: IN_PROGRESS first, then NOT_STARTED, then BLOCKED, then by updatedAt desc
        allSteps.sort((a, b) => {
          const statusOrder: Record<string, number> = {IN_PROGRESS: 0, NOT_STARTED: 1, BLOCKED: 2};
          const sa = statusOrder[a.status] ?? 3;
          const sb = statusOrder[b.status] ?? 3;
          if (sa !== sb) return sa - sb;
          return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
        });

        setSteps(allSteps);
      } catch (e: any) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchAll();
    return () => {
      cancelled = true;
    };
  }, [participantPlanIds, user, client]);

  return {steps, loading, error};
}

// ---------------------------------------------------------------------------
// Generating section (active GUIDE jobs)
// ---------------------------------------------------------------------------

function GeneratingSection() {
  const {user} = useAuth();
  const {activeOrg} = useOrg();
  const navigate = useNavigate();

  const {data} = useQuery(ORG_JOBS, {
    variables: {orgId: activeOrg?.id, status: ['QUEUED', 'RUNNING']},
    skip: !user || !activeOrg?.id,
    pollInterval: 5000,
  });

  const activeGuideJobs = (data?.orgJobs ?? []).filter((j: any) => j.type === 'GUIDE');

  if (activeGuideJobs.length === 0) return null;

  return (
    <Box sx={{mb: 3}}>
      <Typography sx={{fontSize: 13, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1}}>
        Generating
      </Typography>
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
        {activeGuideJobs.map((job: any) => (
          <Box
            key={job.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 2,
              py: 1.5,
              bgcolor: '#fff',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              cursor: 'pointer',
              '&:hover': {bgcolor: '#F9FAFB'},
            }}
            onClick={() => navigate(`/org/jobs/${job.id}`)}
          >
            <CircularProgress size={16} thickness={4} />
            <Typography sx={{flex: 1, fontSize: 14, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
              {job.inputSummary || 'Generating guide…'}
            </Typography>
            <Typography sx={{fontSize: 12, color: '#9CA3AF', flexShrink: 0}}>
              View job →
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Guide picker dialog
// ---------------------------------------------------------------------------

function GuidePickerDialog({open, onClose}: {open: boolean; onClose: () => void}) {
  const navigate = useNavigate();
  const {data, loading} = useQuery(GET_FEATURED_GUIDES, {skip: !open});
  const guides = data?.featuredGuides ?? [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Start from a guide</DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}>
            <CircularProgress size={28} />
          </Box>
        )}
        {!loading && guides.length === 0 && (
          <Box sx={{textAlign: 'center', py: 4}}>
            <Typography sx={{fontSize: 14, color: '#6B7280'}}>
              No published guides available yet.
            </Typography>
            <Button
              variant="outlined"
              sx={{mt: 2}}
              onClick={() => {
                onClose();
                navigate('/explore');
              }}
            >
              Browse public guides
            </Button>
          </Box>
        )}
        {!loading && guides.length > 0 && (
          <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1}}>
            {guides.map((guide: any) => (
              <Card
                key={guide.id}
                variant="outlined"
                sx={{
                  cursor: 'pointer',
                  '&:hover': {borderColor: colors.blue[400], bgcolor: colors.blue[50]},
                  transition: 'all 0.15s ease',
                }}
                onClick={() => {
                  onClose();
                  navigate(buildGuideUrl(guide.orgSlug, guide.slug));
                }}
              >
                <CardContent sx={{py: 1.5, '&:last-child': {pb: 1.5}}}>
                  <Typography sx={{fontWeight: 600, fontSize: 14}}>{guide.title}</Typography>
                  {guide.publisherOrgName && (
                    <Typography sx={{fontSize: 12, color: '#6B7280'}}>{guide.publisherOrgName}</Typography>
                  )}
                  {guide.shortDescription && (
                    <Typography sx={{fontSize: 13, color: '#6B7280', mt: 0.5}}>{guide.shortDescription}</Typography>
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="text"
          onClick={() => {
            onClose();
            navigate('/explore');
          }}
        >
          Browse all guides
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

function PlansPage() {
  useDocumentTitle('My Work');

  const {user} = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [guidePickerOpen, setGuidePickerOpen] = useState(false);

  // Fetch plans list (all statuses so owned plans are visible)
  const {loading: plansLoading, error: plansError, data: plansData} = useQuery(
    GET_MY_ACCESSIBLE_PLANS,
    {
      variables: {status: null, limit: 50, offset: 0},
      skip: !user,
    }
  );

  const plans = plansData?.myAccessiblePlans || [];

  // Fetch assigned steps from participant workspaces
  const {steps: assignedSteps, loading: stepsLoading, error: stepsError} = useAssignedSteps(plans, user);

  const isLoading = plansLoading || (plans.length > 0 && stepsLoading);

  // Client-side search filter
  const filteredSteps = useMemo(() => {
    if (!searchQuery.trim()) return assignedSteps;
    const q = searchQuery.toLowerCase();
    return assignedSteps.filter(
      s => s.title.toLowerCase().includes(q) || s.planTitle.toLowerCase().includes(q)
    );
  }, [assignedSteps, searchQuery]);

  const hasSteps = !isLoading && filteredSteps.length > 0;
  const isEmpty = !isLoading && assignedSteps.length === 0 && !plansError;

  return (
    <Box sx={{maxWidth: 1040, margin: '0 auto', padding: '24px 24px 64px'}}>
      {/* Header */}
      <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3}}>
        <Box>
          <Typography sx={{fontSize: 24, fontWeight: 650, color: colors.textPrimary}}>
            My Work
          </Typography>
          <Typography sx={{fontSize: 14, color: '#6B7280', mt: 0.5}}>
            Steps assigned to you across your active plans.
          </Typography>
        </Box>
        <Box sx={{display: 'flex', gap: 1.5}}>
          <Button variant="contained" startIcon={<ExploreIcon />} onClick={() => setGuidePickerOpen(true)}>
            Start from a guide
          </Button>
        </Box>
      </Box>

      {/* Generating jobs */}
      <GeneratingSection />

      {/* List card */}
      <Box
        sx={{
          bgcolor: '#fff',
          border: '1px solid #E5E7EB',
          borderRadius: '12px',
          boxShadow: '0 1px 2px rgba(0,0,0,.06)',
          overflow: 'hidden',
        }}
      >
        {/* Toolbar */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            borderBottom: '1px solid #E5E7EB',
          }}
        >
          <TextField
            size="small"
            placeholder="Search steps or plans..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{fontSize: 20, color: '#9CA3AF'}} />
                </InputAdornment>
              ),
            }}
            sx={{width: 280}}
          />
          <Chip
            label="Assigned"
            color="primary"
            size="small"
            variant="filled"
            sx={{fontWeight: 600}}
          />
        </Box>

        {/* Table header */}
        <Box
          sx={{
            px: 2,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            borderBottom: '1px solid #E5E7EB',
            bgcolor: '#FAFAFA',
          }}
        >
          {[
            {label: 'Step', flex: 1, width: undefined, align: 'left'},
            {label: 'Plan', flex: undefined, width: 200, align: 'left'},
            {label: 'Updated', flex: undefined, width: 100, align: 'left'},
            {label: 'Status', flex: undefined, width: 110, align: 'left'},
            {label: 'Action', flex: undefined, width: 110, align: 'right'},
          ].map(col => (
            <Typography
              key={col.label}
              sx={{
                flex: col.flex,
                width: col.width,
                fontSize: 12,
                fontWeight: 600,
                color: '#6B7280',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                textAlign: col.align as any,
              }}
            >
              {col.label}
            </Typography>
          ))}
        </Box>

        {/* Body */}
        {isLoading && (
          <Box sx={{display: 'flex', justifyContent: 'center', py: 8}}>
            <CircularProgress />
          </Box>
        )}

        {plansError && (
          <Alert severity="error" sx={{m: 2}}>
            Error loading plans: {plansError.message}
          </Alert>
        )}

        {stepsError && (
          <Alert severity="warning" sx={{m: 2}}>
            Some steps could not be loaded.
          </Alert>
        )}

        {hasSteps &&
          filteredSteps.map(step => {
            const action = deriveActionLabel(step);
            const chip = statusChipProps(step.status);
            return (
              <Box
                key={`${step.planId}-${step.stepId}`}
                sx={{
                  px: 2,
                  py: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  borderBottom: '1px solid #F3F4F6',
                  '&:last-child': {borderBottom: 'none'},
                  '&:hover': {bgcolor: '#F9FAFB'},
                  cursor: 'pointer',
                }}
                onClick={() => navigate(`/app/plans/${step.planId}/participant`)}
              >
                {/* Step */}
                <Box sx={{flex: 1, minWidth: 0, pr: 1}}>
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {step.title}
                  </Typography>
                </Box>

                {/* Plan */}
                <Tooltip title={step.planTitle}>
                  <Typography
                    sx={{
                      width: 200,
                      fontSize: 13,
                      color: '#6B7280',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                    }}
                    onClick={e => {
                      e.stopPropagation();
                      navigate(`/app/plans/${step.planId}`);
                    }}
                  >
                    {step.planTitle}
                  </Typography>
                </Tooltip>

                {/* Updated */}
                <Tooltip title={new Date(step.updatedAt).toLocaleString()}>
                  <Typography
                    sx={{width: 100, fontSize: 13, color: '#6B7280', flexShrink: 0}}
                  >
                    {formatRelativeDate(step.updatedAt)}
                  </Typography>
                </Tooltip>

                {/* Status */}
                <Box sx={{width: 110, flexShrink: 0}}>
                  <Chip
                    label={chip.label}
                    color={chip.color}
                    size="small"
                    variant="outlined"
                    sx={{height: 24, fontSize: 12}}
                  />
                </Box>

                {/* Action */}
                <Box sx={{width: 110, flexShrink: 0, textAlign: 'right'}}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={action.icon}
                    onClick={e => {
                      e.stopPropagation();
                      navigate(`/app/plans/${step.planId}/participant`);
                    }}
                    sx={{textTransform: 'none', fontSize: 13}}
                  >
                    {action.label}
                  </Button>
                </Box>
              </Box>
            );
          })}

        {/* Filtered-empty (search has no matches but steps exist) */}
        {!isLoading && assignedSteps.length > 0 && filteredSteps.length === 0 && (
          <Box sx={{py: 6, textAlign: 'center'}}>
            <Typography sx={{fontSize: 14, color: '#6B7280'}}>
              No steps match "{searchQuery}"
            </Typography>
          </Box>
        )}

        {/* Empty state */}
        {isEmpty && (
          <Box sx={{py: 6, px: 3, textAlign: 'center'}}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '999px',
                bgcolor: colors.green[50],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
              }}
            >
              <CheckCircleOutlineIcon sx={{color: colors.green[600], fontSize: 24}} />
            </Box>

            <Typography sx={{fontSize: 18, fontWeight: 600, mt: 2}}>
              You're all caught up
            </Typography>
            <Typography sx={{fontSize: 14, color: '#6B7280', maxWidth: 380, mx: 'auto', mt: 1, lineHeight: 1.5}}>
              No open steps assigned to you right now. Start a new journey from a guide or check back later.
            </Typography>

            <Box sx={{mt: 3, display: 'flex', justifyContent: 'center', gap: 1.5}}>
              <Button
                variant="contained"
                startIcon={<ExploreIcon />}
                onClick={() => setGuidePickerOpen(true)}
              >
                Start from a guide
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      {/* My Plans section */}
      {!plansLoading && plans.length > 0 && (
        <Box sx={{mt: 4}}>
          <Typography sx={{fontSize: 18, fontWeight: 600, color: colors.textPrimary, mb: 1.5}}>
            My Plans
          </Typography>
          <Box
            sx={{
              bgcolor: '#fff',
              border: '1px solid #E5E7EB',
              borderRadius: '12px',
              boxShadow: '0 1px 2px rgba(0,0,0,.06)',
              overflow: 'hidden',
            }}
          >
            <Box sx={{display: 'flex', flexDirection: 'column'}}>
              {plans.map((plan: PlanListItemData) => (
                <PlanListRow key={plan.id} plan={plan} />
              ))}
            </Box>
          </Box>
        </Box>
      )}

      {/* Guide picker dialog */}
      <GuidePickerDialog open={guidePickerOpen} onClose={() => setGuidePickerOpen(false)} />
    </Box>
  );
}

export default PlansPage;
